import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import Configs from '@/Configs';
import { fetchOptions, handleResponse } from '@/common/apiCalls';
import { canonicalizeCompanyId } from '@/common/canonicalizeCompanyId';
import { normalizeCompanyPreferencesResponse } from '@/common/companyPreferencesCache';
import type { CompanyPreferences } from '@/common/dataContext';
import { queriesKeys } from '@/queries/queries-keys';
import { useBasicQueryParams } from '@/queries/useBasicQueryParams';

export type GetCompanyPreferencesQueryConfig = Omit<
  UseQueryOptions<CompanyPreferences, Error, CompanyPreferences>,
  'queryKey' | 'queryFn'
>;

/**
 * Company preferences / module config (`GET companyPreferences/`).
 *
 * Set `staleTime: Infinity` by default — company config is essentially static for the
 * duration of a session and the endpoint is not triggered by any Career mutation.
 * Callers may override via `queryConfig`.
 */
export function useGetCompanyPreferences(
  queryConfig: GetCompanyPreferencesQueryConfig = {}
): UseQueryResult<CompanyPreferences, Error> {
  const { companyID, employeeID, token, correlationId, basicEnabled } = useBasicQueryParams();
  const { enabled = true, staleTime = Infinity, ...restQueryConfig } = queryConfig;
  const canonicalCompanyID = canonicalizeCompanyId(companyID);

  return useQuery<CompanyPreferences, Error>({
    ...restQueryConfig,
    staleTime,
    enabled: enabled && basicEnabled && canonicalCompanyID != null,
    queryKey: queriesKeys.getCompanyPreferences(canonicalCompanyID),
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('companyID', String(canonicalCompanyID));
      qs.set('employeeID', String(employeeID));

      const response = await fetch(
        `${Configs.platformGetCompanyPreferencesAPI}?${qs.toString()}`,
        fetchOptions('GET', null, token, 'json', correlationId)
      );

      const result = await handleResponse(response);
      // API sends `[doc]`; login/session use a single doc — never store the array in cache.
      return normalizeCompanyPreferencesResponse(result);
    },
  });
}
